import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Sprout, Loader2 } from "lucide-react";

export default function Auth() {
  const { login, signup } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";
  const sellerIntent = from.includes("/sell");
  const [tab, setTab] = useState(sellerIntent ? "signup" : "login");
  const [loading, setLoading] = useState(false);

  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [signupData, setSignupData] = useState({
    email: "", password: "", full_name: "", phone: "", role: sellerIntent ? "seller" : "shopper", default_postcode: "",
  });

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success("Welcome back!");
      navigate(from);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  const doSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await signup(signupData);
      toast.success("Account created!");
      navigate(u.role === "seller" ? "/sell/register" : from);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen pt-16 bg-grain" style={{ background: "radial-gradient(1000px 500px at 20% 0%, rgba(143,181,115,0.15), transparent 60%)" }}>
      <div className="max-w-md mx-auto px-4 py-10">
        <div className="flex flex-col items-center mb-6">
          <img src="/logo-icon.png" alt="Farm Gate" className="h-20 w-auto mb-2" />
          <img src="/logo-wordmark.png" alt="Farm Gate" className="h-8 w-auto" />
        </div>

        <Card className="rounded-2xl shadow-md">
          <CardHeader className="pb-2">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="login" data-testid="auth-tab-login">Sign in</TabsTrigger>
                <TabsTrigger value="signup" data-testid="auth-tab-signup">Create account</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={doLogin} className="space-y-4 pt-4">
                  <div className="space-y-1.5">
                    <Label>Email</Label>
                    <Input type="email" required value={loginData.email} data-testid="auth-email-input"
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })} placeholder="you@example.com" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Password</Label>
                    <Input type="password" required value={loginData.password} data-testid="auth-password-input"
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })} placeholder="••••••••" />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="auth-login-submit">
                    {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Sign in
                  </Button>
                  <p className="text-xs text-muted-foreground text-center">Try seller@test.com / Password123!</p>
                </form>
              </TabsContent>

              <TabsContent value="signup">
                <form onSubmit={doSignup} className="space-y-3.5 pt-4">
                  <div className="space-y-1.5">
                    <Label>Full name</Label>
                    <Input required value={signupData.full_name} data-testid="signup-name-input"
                      onChange={(e) => setSignupData({ ...signupData, full_name: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Email</Label>
                    <Input type="email" required value={signupData.email} data-testid="signup-email-input"
                      onChange={(e) => setSignupData({ ...signupData, email: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Password</Label>
                    <Input type="password" required value={signupData.password} data-testid="signup-password-input"
                      onChange={(e) => setSignupData({ ...signupData, password: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Phone (optional)</Label>
                    <Input value={signupData.phone}
                      onChange={(e) => setSignupData({ ...signupData, phone: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>I want to join as</Label>
                    <RadioGroup value={signupData.role} onValueChange={(v) => setSignupData({ ...signupData, role: v })} className="grid grid-cols-2 gap-2">
                      <label className={`flex items-center gap-2 rounded-xl border p-3 cursor-pointer ${signupData.role === 'shopper' ? 'border-primary bg-secondary' : 'border-border'}`}>
                        <RadioGroupItem value="shopper" data-testid="signup-role-shopper" /> Shopper
                      </label>
                      <label className={`flex items-center gap-2 rounded-xl border p-3 cursor-pointer ${signupData.role === 'seller' ? 'border-primary bg-secondary' : 'border-border'}`}>
                        <RadioGroupItem value="seller" data-testid="signup-role-seller" /> Farmer
                      </label>
                    </RadioGroup>
                  </div>
                  <Button type="submit" className="w-full bg-[color:var(--fg-wood-600)] hover:bg-[color:var(--fg-wood-700)] text-white" disabled={loading} data-testid="auth-signup-submit">
                    {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create account
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardHeader>
          <CardContent />
        </Card>
        <p className="text-center text-sm text-muted-foreground mt-4">
          <Link to="/" className="underline">Back to map</Link>
        </p>
      </div>
    </div>
  );
}
